import { apiGet } from './client';
import type { OperationResult } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Run evidence read like a debugger, and what a run page leads with (Scenario Intelligence SI-03a / SI-03b).
//
// Mirrors OO.Business.AIInfo.Validation: RunEvidence.cs (a turn's model calls, rounds, brief, skills, searches,
// consultations), TurnTimeline.cs (the turn in observed order, with what was available before each response),
// ObligationEvidence.cs (every check as expected against observed) and RunReading.cs (states, identity, cost by
// role, the pre-run preview). Read-only. Every judgement is the server's; the screens print it and never fill a gap:
// "not recorded" is not "did not happen", and an unpriced call is never $0.
// ─────────────────────────────────────────────────────────────────────────────

export type EvidenceRole = 'Agent' | 'Router' | 'Peer' | 'Other';

export interface BriefElementEvidence {
  briefingCode: string;
  elementCode: string;
  /** Rendered, Empty, Deduped, or a fault such as Renderer-Timeout (the briefing audit's own words). */
  state: string;
  dedupedFrom?: string | null;
  tokenEstimate: number;
}

export interface LoadedSkillEvidence {
  skillId: number;
  name: string;
  /** AlwaysLoad, RouterPicked, Advertised … */
  loadSource: string;
}

/** One model round, as measured before the request left: sizes and hashes only, never text. */
export interface ModelRoundEvidence {
  roundIndex: number;
  startedUtc?: string | null;
  durationMs?: number | null;
  model?: string | null;
  outcome?: string | null;
  finishReason?: string | null;
  /** Provider-reported; null = reported nothing (not zero). */
  inputTokens?: number | null;
  cachedInputTokens?: number | null;
  outputTokens?: number | null;
  reasoningTokens?: number | null;
  /** All sent characters ÷ 4: an estimate. */
  estimatedInputTokens: number;
  toolsOffered: number;
  toolsInvoked: number;
  toolResultCount: number;
  briefingChars: number;
  skillChars: number;
  retrievedContextChars: number;
  retrievalResultChars: number;
  /** SHA-256 of the system prefix this round sent. */
  promptHash?: string | null;
  toolSetHash?: string | null;
}

export interface ModelCallEvidence {
  promptLogId: number;
  role: EvidenceRole;
  agentId?: number | null;
  /** The deployment as the call recorded it. */
  model?: string | null;
  route?: string | null;
  inputTokens: number;
  outputTokens: number;
  cachedTokens: number;
  reasoningTokens: number;
  /** Null = no rate was found: unpriced, never $0. */
  costUsd?: number | null;
  durationMs: number;
  statusCode: number;
  failed: boolean;
  toolCallCount: number;
  brief: BriefElementEvidence[];
  skills: LoadedSkillEvidence[];
  loggedUtc?: string | null;
  rounds?: ModelRoundEvidence[] | null;
}

export interface RetrievedPassageEvidence {
  chunkId?: string | null;
  documentId?: string | null;
  fileName?: string | null;
  page?: number | null;
  score?: number | null;
  rerankerScore?: number | null;
  snippet?: string | null;
}

export interface KnowledgeSearchEvidence {
  outcome?: string | null;
  knowledgeIds?: string | null;
  resultCount: number;
  topScore?: number | null;
  durationMs?: number | null;
  passages: RetrievedPassageEvidence[];
  startedUtc?: string | null;
  agentId?: number | null;
  query?: string | null;
}

export interface ConsultationEvidence {
  peer?: string | null;
  peerAgentId?: number | null;
  succeeded: boolean;
  timedOut: boolean;
  error?: string | null;
  depth: number;
  elapsedMs: number;
  startedUtc?: string | null;
  question?: string | null;
  reply?: string | null;
  replyCut?: boolean;
}

export interface RunCallTotals {
  calls: number;
  inputTokens: number;
  outputTokens: number;
  /** Null when any call is unpriced. */
  costUsd?: number | null;
  unpricedCalls: number;
}

export interface TurnRuntimeEvidence {
  modelCalls: ModelCallEvidence[];
  knowledgeSearches: KnowledgeSearchEvidence[];
  consultations: ConsultationEvidence[];
  totals: RunCallTotals;
  notes: string[];
  /** False for a turn recorded before run evidence existed: "Not recorded", never "none". */
  recorded?: boolean;
}

export type TimelineKind = 'Router' | 'ModelRound' | 'Tool' | 'KnowledgeSearch' | 'Consultation';

export interface SkillInPrompt {
  name: string;
  /** AlwaysLoad, RouterPicked … or LoadSkill (the model loaded it itself, see toolOrder). */
  source: string;
  toolOrder?: number | null;
}

export interface AvailableBefore {
  briefElements: number;
  briefRendered: number;
  briefEmpty: number;
  briefDeduped: number;
  briefFaults: number;
  skills: SkillInPrompt[];
  toolResults: number[];
  knowledgeSearches: number[];
  consultations: number[];
  toolsOffered?: number | null;
  estimatedInputTokens?: number | null;
  promptHash?: string | null;
  samePromptPrefix?: boolean | null;
  newSkills: SkillInPrompt[];
  newToolResults: number[];
  newKnowledgeSearches: number[];
  newConsultations: number[];
}

export interface TimelineStep {
  kind: TimelineKind;
  /** Role for model rounds and searches; the tool name for a tool; the peer for a consultation. */
  actor?: string | null;
  /** Milliseconds after the turn's first recorded step; null = no recorded start. */
  atMs?: number | null;
  durationMs?: number | null;
  startApproximate: boolean;
  concurrent: boolean;
  callIndex?: number | null;
  roundIndex?: number | null;
  toolOrder?: number | null;
  searchIndex?: number | null;
  consultationIndex?: number | null;
  /** The consultation tool call this step happened inside: a peer's own work. */
  withinToolOrder?: number | null;
  /** On the agent's own model rounds: what was available before this response. */
  available?: AvailableBefore | null;
}

export interface TurnTimeline {
  steps: TimelineStep[];
  notes: string[];
}

/** AwaitingReview: an obligation a reviewer judges (contract v3). NotApplicable: a reviewed rule says it does not apply. */
export type ObligationState = 'Pass' | 'Fail' | 'NotObserved' | 'NotAssessed' | 'AwaitingReview' | 'NotApplicable';

export interface ObligationEvidence {
  checkId: string;
  scope: 'Turn' | 'Conversation' | string;
  turnIndex?: number | null;
  rule?: string | null;
  kind?: string | null;
  operator: string;
  /** Exact, Weak (a phrase check) or Manual (a reviewer judges it; no run marks it passed). */
  strength: string;
  expected: string;
  state: ObligationState;
  observed: string;
  failures: { check: string; detail: string }[];
  /** Null = no reviewed requirement linked: the expectation is pending SME review. */
  requirementRef?: string | null;
}

export interface UnplacedFailure {
  turnIndex?: number | null;
  rule?: string | null;
  failure: { check: string; detail: string };
  why: string;
}

export interface ObligationReading {
  versionRead: boolean;
  obligations: ObligationEvidence[];
  unplaced: UnplacedFailure[];
  notes: string[];
}

export interface RunIdentity {
  runId: number;
  agentId: number;
  agentName?: string | null;
  agentCanonicalName?: string | null;
  /** The configuration hash read before the first scenario; null for runs before manifests. */
  candidateHash?: string | null;
  configuredModel?: string | null;
  configuredDeployment?: string | null;
  apiTitle?: string | null;
  modelSource?: string | null;
  merchantId?: number | null;
}

/** AwaitingReview (contract v3): every automatic check passed, and an obligation a reviewer judges waits for one. */
export type ResultStateName = 'NeedsAttention' | 'AwaitingReview' | 'EvidenceIncomplete' | 'NotAssessed' | 'Passed';

export interface ResultState {
  resultId: number;
  scenarioId: string;
  state: ResultStateName;
  reason: string;
  exploratory: boolean;
}

export interface RunResultsSummary {
  libraryScenarios: number;
  needsAttention: number;
  evidenceIncomplete: number;
  notAssessed: number;
  passed: number;
  awaitingReview?: number;
  exploratory: number;
  exploratoryPassed: number;
  exploratoryNeedsAttention: number;
}

export interface DeploymentCount {
  deployment?: string | null;
  calls: number;
}

export interface RoleUsage {
  role: EvidenceRole;
  calls: number;
  inputTokens: number;
  cachedTokens: number;
  outputTokens: number;
  /** The priced calls only; null when none was priced. */
  pricedCostUsd?: number | null;
  unpricedCalls: number;
  deployments: DeploymentCount[];
}

export interface RunUsageReading {
  tracedTurns: number;
  turns: number;
  roles: RoleUsage[];
  pricedCostUsd?: number | null;
  unpricedCalls: number;
  deploymentCheck: string;
  deploymentMatched?: boolean | null;
}

export interface RunTiming {
  endToEndMs?: number | null;
  medianScenarioMs?: number | null;
  scenarios: number;
  medianModelCallMs?: number | null;
  modelCalls: number;
}

export interface RunReading {
  identity: RunIdentity;
  results: ResultState[];
  summary: RunResultsSummary;
  usage: RunUsageReading;
  timing: RunTiming;
  notes: string[];
  /** World seeder W5: the evaluation worlds the run's results ran in; absent or empty when none did. */
  worlds?: RunWorld[] | null;
}

/** An evaluation world a run's results ran in: the role in words, the day it was built for, its patient and course, how it ended. */
export interface RunWorld {
  worldId: number;
  role: string;
  words: string;
  practiceDate: string;
  memberId: number;
  memberProtocolId?: number | null;
  /** Seeding, Ready or TornDown. */
  state: string;
  problem?: string | null;
  scenarioIds: string[];
}

export interface PlannedScenario {
  scenarioId: string;
  title?: string | null;
  lifecycleStage?: string | null;
  /** A draft: its result runs as Exploratory and never counts toward release. */
  exploratory: boolean;
  snapshotId?: number | null;
}

export interface RunEstimate {
  scenarios: number;
  estimated: number;
  lowUsd?: number | null;
  highUsd?: number | null;
  resultsRead: number;
  unpriced: string[];
  noHistory: string[];
  latestRunId?: number | null;
  latestRun: RoleUsage[];
  notes: string[];
}

export interface RunPreview {
  scenarios: PlannedScenario[];
  estimate: RunEstimate;
}

/** What a run would do before it starts: the scenarios it would run and a cost estimate from their earlier results. */
export async function getRunPreview(agentId: number, scenarioId?: string, signal?: AbortSignal): Promise<RunPreview> {
  const wrapped = await apiGet<OperationResult<RunPreview>>(
    `/api/ValidationRuns/Agents/${agentId}/RunPreview`,
    scenarioId ? { scenarioId } : undefined,
    { signal },
  );
  if (!wrapped.isSuccess || !wrapped.data) throw new Error(wrapped.message || 'Could not read what this run would do');
  return wrapped.data;
}

// ── Wording shared by the run page, the finding drawer and the Debugger ──────

/** "Recorded priced usage: $X, plus N unpriced calls" — the one way a recorded cost is written. */
export function recordedUsage(pricedUsd: number | null | undefined, unpricedCalls: number): string {
  const priced = pricedUsd == null ? null : formatUsd(pricedUsd);
  if (priced == null && unpricedCalls === 0) return 'No recorded usage';
  if (priced == null) return `No priced usage recorded: ${unpricedCalls} unpriced call${unpricedCalls === 1 ? '' : 's'}`;
  if (unpricedCalls === 0) return `Recorded priced usage: ${priced}`;
  return `Recorded priced usage: ${priced}, plus ${unpricedCalls} unpriced call${unpricedCalls === 1 ? '' : 's'}`;
}

/** Dollars with enough precision for a single model round. */
export function formatUsd(usd: number): string {
  if (usd === 0) return '$0.00';
  if (usd < 0.01) return `$${usd.toFixed(4)}`;
  if (usd < 1) return `$${usd.toFixed(3)}`;
  return `$${usd.toFixed(2)}`;
}

export function formatMs(ms: number | null | undefined): string {
  if (ms == null) return '—';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  const m = Math.floor(ms / 60_000);
  const s = Math.round((ms % 60_000) / 1000);
  return s ? `${m} min ${s} s` : `${m} min`;
}

export function shortHash(hash: string | null | undefined): string | null {
  if (!hash) return null;
  return hash.length > 12 ? `${hash.slice(0, 4)}…${hash.slice(-4)}` : hash;
}

export const RESULT_STATE_LABEL: Record<ResultStateName, string> = {
  NeedsAttention: 'Needs attention',
  AwaitingReview: 'Awaiting review',
  EvidenceIncomplete: 'Evidence incomplete',
  NotAssessed: 'Not assessed',
  Passed: 'Passed applicable checks',
};

export const OBLIGATION_STATE_LABEL: Record<ObligationState, string> = {
  Pass: 'Pass',
  Fail: 'Fail',
  NotObserved: 'Not observed',
  NotAssessed: 'Not assessed',
  AwaitingReview: 'Awaiting review',
  NotApplicable: 'Not applicable',
};
